import React from 'react';
import classes from './DocumentActivity.module.scss';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Align,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import colors from 'scss/variables/colors.module.scss';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'store/hook';
import { IDocsActivityItem } from 'interfaces/AnalyticTypes';
import analytics from 'api/analytics';
import { Skeleton } from 'antd';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const customFormatter = (value: number, index: any, all: number[]) => {
  const showValue = value > 0 && value !== all[index.dataIndex];
  return showValue ? value : '';
};

const customAlign = (context: any, newItems: number[]) => {
  const maxValueOfDataset = Math.max(...context.dataset.data.filter(Number));
  const currentValue = context.dataset.data[context.dataIndex];
  const isLargeValue = currentValue > maxValueOfDataset * 0.9;

  const showStartAligh = newItems[context.dataIndex] !== 0 || maxValueOfDataset === currentValue || isLargeValue
  return showStartAligh
    ? 'start' as Align
    : 'end' as Align;
};

const dataLabelConfig = (isDark: boolean) => ({
  color: isDark ? colors.activegrey : colors.activegreydark,
  display: 'auto',
  font: {
    size: 14,
  },
  formatter: (value: number, index: any) => value > 0 ? value : '',
  // anchor: 'end' as Align,
  // align: 'center' as Align,
});

const options = {
  responsive: true,
  maintainAspectRatio: false,
  indexAxis: 'y' as const,
  elements: {
    bar: {
      borderWidth: 2,
    },
  },
  scales: {
    x: {
      // stacked: true,
      ticks: {
        // stepSize: 1,
        precision: 0
      },
    },
    y: {
      stacked: true,
      // ticks: {
      //   font: {
      //     size: 14,
      //   },
      // },
    },
  },
  plugins: {
    title: {
      display: true,
      text: 'Bar Chart - Stacked',
      font: {
        size: 14,
      },
    },
    // datalabels: dataLabelConfig,
    legend: {
      // display: true,
      // position: 'bottom' as const,
      labels: {
        font: {
          size: 14,
        },
      },
      onClick: (e: any, legendItem: any, legend: any) => {
        // const index = legendItem.datasetIndex;
        // const ci = legend.chart;
        // if (ci.isDatasetVisible(index)) {
        //   ci.hide(index);
        //   legendItem.hidden = true;
        // } else {
        //   ci.show(index);
        //   legendItem.hidden = false;
        // }
      }
    },
  },
};

// const mockData = [
//   {room_name: 'Комната 1 (под NDA)', documents_count: 5, new_documents: 1, documents_actions: 290, new_actions: 0},
//   {room_name: 'Комната 2', documents_count: 30, new_documents: 0, documents_actions: 300, new_actions: 0},
//   {room_name: 'Комната 3', documents_count: 8, new_documents: 1, documents_actions: 18, new_actions: 2},
// ];


type ShareTableProps = {
  period: [string, string, string];
  choosenRoom?: string;
}

const DocumentActivity = ({choosenRoom, period}: ShareTableProps) => {
  const [height, setHeight] = React.useState(0);
  const [isLoadingDocsActivity, setIsLoadingDocsActivity] = React.useState(false);
  const [docsActivity, setDocsActivity] = React.useState<IDocsActivityItem[]>([]);
  
  const wrapElem = React.useRef<HTMLDivElement | null>(null);
  
  const { isDarkMode, messageApi } = useAppSelector((store) => store.windowState);
  const { t } = useTranslation();

  const getTempLinks = async (period: [string, string, string], room?: string) => {
    setIsLoadingDocsActivity(true);
    try {
      const tempLinks = await analytics.getAnalyticsDocumentsActivity({room, from: period[0], to: period[1]});  
      setDocsActivity(tempLinks.data);
    } catch (e) {
      messageApi?.error(t('Analytics.errors.document_activity'), 6)
    } finally {
      setIsLoadingDocsActivity(false);
    };
  };

  React.useEffect(() => {
    getTempLinks(period, choosenRoom);
  }, [choosenRoom, period[2]]);

  const updateSize = () => setHeight(Number(wrapElem.current?.offsetHeight));

  React.useEffect(() => {
    if (wrapElem.current) {
      window.addEventListener('resize', updateSize);
      updateSize();
    }
    return () => window.removeEventListener('resize', updateSize);
  }, [wrapElem.current]);
    
  const proportion = Math.ceil(docsActivity?.length/4);
  const ratioProportion = `4/${proportion}`;
  
  let options1 = {
    ...options,
    plugins: {
      ...options.plugins,
      title: {
        ...options.plugins.title,
        text: t('Analytics.docsActivity.docsTitle'),
      }
    }
  };
  let options2 = {
    ...options,
    plugins: {
      ...options.plugins,
      title: {
        ...options.plugins.title,
        text: t('Analytics.docsActivity.actionsTitle'),
      }
    }
  };
  
  let labels: any = [];
  let allFiles: any[] = [];
  let newFiles: number[] = [];
  let allActions: number[] = [];
  let newActions: number[] = [];

  docsActivity?.forEach((room:any) => {
    if (room.room_name?.length > 30 && room.room_name?.length < 60) {
      let mid = Math.floor(room.room_name.length / 2); 
      let firstHalf = room.room_name.substring(0, mid);
      let secondHalf = room.room_name.substring(mid);
      labels.push([firstHalf, secondHalf]);
    } else if (room.room_name?.length > 60) {
      let mid = Math.floor(room.room_name.length / 3); 
      let firstPart = room.room_name.substring(0, mid);
      let secondPart = room.room_name.substring(mid, mid*2);
      labels.push([firstPart, `${secondPart}...`]);
    } else {
      labels.push(room.room_name);
    }

    allFiles.push(room.documents_count);
    newFiles.push(room.new_documents);
    allActions.push(room.documents_actions);
    newActions.push(room.new_actions);
  });

  const dataFiles = {
    labels,
    datasets: [
      {
        datalabels: {
          ...dataLabelConfig(isDarkMode),
          anchor: 'end' as Align,
          align: (context: any) => customAlign(context, newFiles),
        },
        label: t('Analytics.docsActivity.documents_count'),
        data: allFiles,
        backgroundColor: isDarkMode ? colors.primary100 : colors.primarytransparent,
        borderColor: isDarkMode ? colors.primary100Dark : colors.primary100,
        order: 2,
        borderRadius: 8,
        // stack: 'files',
      },
      {
        datalabels: {
          ...dataLabelConfig(isDarkMode),
          formatter: (e: number, i: any) => customFormatter(e,i,allFiles),
          anchor: 'start' as Align,
          align: 'end' as Align
        },
        label: t('Analytics.docsActivity.new_documents'),
        data: newFiles,
        backgroundColor: isDarkMode ? colors.success : colors.successTransparent,
        borderColor: isDarkMode ? colors.excel : colors.success,
        order: 1,
        borderRadius: 8,
        // stack: 'files',
      },
    ],
  };

  const dataActions = {
    labels,
    datasets: [
      {
        datalabels: {
          ...dataLabelConfig(isDarkMode),
          anchor: 'end' as Align,
          align: (context: any) => customAlign(context, newActions),
        },
        label: t('Analytics.docsActivity.documents_actions'),
        data: allActions,
        backgroundColor: isDarkMode ? colors.primary100 : colors.primarytransparent,
        borderColor: isDarkMode ? colors.primary100Dark : colors.primary100,
        order: 2,
        borderRadius: 8,
        // stack: 'actions',
      },
      {
        datalabels: {
          ...dataLabelConfig(isDarkMode),
          formatter: (e: number, i: any) => customFormatter(e,i,allActions),
          anchor: 'start' as Align,
          align: 'end' as Align,
          // display: customDisplay,
        },
        label: t('Analytics.docsActivity.new_actions'),
        data: newActions,
        backgroundColor: isDarkMode ? colors.success : colors.successTransparent,
        borderColor: isDarkMode ? colors.excel : colors.success,
        order: 1,
        borderRadius: 8,
        // stack: 'actions',
      },
    ],
  };  

  return (
    <div ref={wrapElem} style={{aspectRatio: ratioProportion}} className={classes.docsActivity}>
      <div  className={classes.segment}>
        {isLoadingDocsActivity
          ? <Skeleton.Input active block style={{margin: '30px 0 0 20px', width: '93%', height: height - 50}} />
          : <Bar options={options1} data={dataFiles} plugins={[ChartDataLabels]}/>
        }
      </div>
      <div className={classes.segment}>
        {isLoadingDocsActivity
          ? <Skeleton.Input active block style={{margin: '30px 0 0 20px', width: '93%', height: height - 50}} />
          : <Bar options={options2} data={dataActions} plugins={[ChartDataLabels]}/>
        }
      </div>
    </div>
  );
}

export default DocumentActivity;
